import { StandardEvents } from '@shopify/events';

// PDP behaviors on top of the native Horizon product form (ported from the
// Lettoria campaign layer, preorder layer excluded - unresolved with anh):
// - pair add-on toggle: adds the companion via /cart/add.js right before the
//   native submit so the drawer opens showing both items
// - ATC label & sticky-bar price mirror the with-code amounts from the
//   per-variant map emitted by buy-buttons.liquid (data-skullette-prices)
// - re-assert after every variant morph: picker change fires instantly, then
//   ProductSelectEvent's promise + setTimeout(0) re-applies once Horizon's own
//   listeners finish morphing (missing this repaints base prices - locked).

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

// Set-picker verb override (single writer stays refreshTotals): a ticked set
// holding ANY in-stock piece reads as a normal purchase - something is in
// hand today, so "reserve" language would lie. All-pre-order keeps the
// promise verb; untick-all clears the override and the anchor's own verb
// (data-sk-atc-verb) rules again.
let setVerbOverride = null;

function variantPrices(section) {
  const script = section.querySelector('script[data-skullette-prices]');
  if (!script) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

// The checked picker input carries data-variant-id synchronously at click
// time (the hidden id input only updates after the native fetch morphs it).
function selectedVariantId(section) {
  const checked = section.querySelector('variant-picker input:checked[data-variant-id]');
  if (checked) return checked.dataset.variantId;
  return section.querySelector('product-form-component input[name="id"]')?.value ?? null;
}

function selectedVariantAvailable(section) {
  const picker = section.querySelector('variant-picker');
  if (picker) {
    const checked = picker.querySelector('input:checked');
    if (checked?.dataset.optionAvailable) return checked.dataset.optionAvailable !== 'false';
    return true;
  }
  const button = section.querySelector('product-form-component button[name="add"]');
  return !button?.disabled;
}

function repriceCouponStrips(section, cents) {
  for (const strip of section.querySelectorAll('[data-coupon-strip]')) {
    strip.dispatchEvent(new CustomEvent('skullette:coupon:price', { bubbles: true, detail: { price: cents } }));
  }
}

function refreshTotals(section) {
  const nowEl = section.querySelector('.pban__now');
  const wasEl = section.querySelector('.pban__old');
  const addon = section.querySelector('[ref-pair-addon]:not(.is-off)');
  if (!nowEl) return;

  // Sold out: the native form already disabled the button and printed its
  // own label - never paint "Add to Bag - $X" over it (Lettoria s24 lesson).
  const available = selectedVariantAvailable(section);

  const prices = variantPrices(section)?.[selectedVariantId(section)] ?? null;
  const priceWrap = section.querySelector('.sk-buy-head__price');
  if (prices) {
    nowEl.textContent = money(prices.now);
    // "was" exists only on clearance variants; a product can mix clearance
    // and normal variants, so the strike + badge follow the selection.
    if (wasEl && prices.was != null) wasEl.textContent = money(prices.was);
    if (priceWrap) priceWrap.classList.toggle('is-clear', prices.was != null);
    // The claim strip (skullette-coupon-strip.js) recomputes its saving and
    // "You pay" from the selected variant's list price.
    repriceCouponStrips(section, prices.now);
  }
  // A claimed coupon repaints the headline to the code price
  // (skullette-coupon-strip.js) and parks the list price on data-list-cents.
  const listCents = parseInt(nowEl.dataset.listCents, 10);
  const baseCents = prices ? prices.now
    : !Number.isNaN(listCents) ? listCents
    : Math.round(parseFloat(nowEl.textContent.replace(/[^0-9.]/g, '')) * 100);
  const addonCents = addon ? parseInt(addon.getAttribute('data-price'), 10) : 0;
  if (Number.isNaN(baseCents) || !available) return;

  const atcText = Array.from(section.querySelectorAll('.add-to-cart-text__content > span')).find(
    (el) => !el.closest('sticky-add-to-cart')
  );
  const verb =
    setVerbOverride ?? (section.querySelector('product-form-component')?.dataset.skAtcVerb || 'Add to Bag');
  if (atcText) {
    // Pre-order variants keep their verb (Liquid sets data-sk-atc-verb under
    // the app-metafield gate) - the price repaint must not erase the promise.
    // A ticked set prices the whole selection: the picker stamps the sum on
    // the button, and the label must agree with the "Your set" total line.
    const setCents = parseInt(atcText.closest('button')?.getAttribute('data-set-base-cents'), 10);
    atcText.textContent = `${verb} · ${money(Number.isNaN(setCents) ? baseCents + addonCents : setCents)}`;
  }

  // Sticky bar clones the same verb server-side; keep the clone in step so a
  // mixed set never reads "Pre-order" up top while the buy box says Add to
  // Bag. Idempotent: without an override this writes the value already there.
  const stickyVerb = document.querySelector('sticky-add-to-cart .add-to-cart-text__content > span');
  if (stickyVerb) stickyVerb.textContent = verb;

  const stickyPrice = document.querySelector('.sticky-add-to-cart__price');
  if (stickyPrice) {
    const showWas = wasEl && (!priceWrap || priceWrap.classList.contains('is-clear'));
    stickyPrice.innerHTML = showWas ? `<s>${wasEl.textContent}</s> ${nowEl.textContent}` : nowEl.textContent;
  }
}

// Returns true when the companion actually landed in the cart; a 422 (sold
// out since render) or 429 (throttling, observed live) must NOT leave the
// button reading "Added" (reviewer H3).
async function addCompanion(addon) {
  try {
    const body = new FormData();
    body.append('items[0][id]', addon.getAttribute('data-variant-id'));
    body.append('items[0][quantity]', '1');
    // Pre-order companion: carry the same promise properties as the main form
    // (every add path must - orders otherwise mix labeled/unlabeled lines).
    if (addon.dataset.preorderBatch) {
      body.append('items[0][properties][_preorder]', 'true');
      body.append('items[0][properties][_preorder_batch]', addon.dataset.preorderBatch);
      if (addon.dataset.preorderEta) body.append('items[0][properties][_preorder_eta]', addon.dataset.preorderEta);
    }
    const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    if (!res.ok) throw new Error(`cart/add ${res.status}`);
    return true;
  } catch (error) {
    console.error('pair add-on add failed', error);
    addon.classList.add('is-off');
    addon.setAttribute('aria-pressed', 'false');
    return false;
  }
}

// Pre-order state is per VARIANT (the app arms Top-Grain while Faux stays a
// normal sale on the red-interior handbags). Horizon's variant switch only
// morphs the picker + the hidden id input, so the three server-rendered
// carriers of that state went stale: the batch register (.sk-porder), the
// ATC verb (data-sk-atc-verb) and the _preorder* line properties the order
// must carry. Re-seat all three from the freshly fetched document.
function syncPreorderState(section, html) {
  const form = section.querySelector('product-form-component');
  const nextForm = html.querySelector('product-form-component');
  if (!form || !nextForm) return;

  // verb: refreshTotals reads it back for both the main button and sticky bar
  const verb = nextForm.getAttribute('data-sk-atc-verb');
  if (verb) form.setAttribute('data-sk-atc-verb', verb);
  else form.removeAttribute('data-sk-atc-verb');

  // line properties: the order's promise must match the variant being added
  form.querySelectorAll('input[name^="properties[_preorder"]').forEach((el) => el.remove());
  const idInput = form.querySelector('input[name="id"]');
  const nextProps = Array.from(nextForm.querySelectorAll('input[name^="properties[_preorder"]'));
  if (idInput) nextProps.reverse().forEach((el) => idInput.after(el.cloneNode(true)));

  // batch register: replace in place, drop, or insert above the button row
  const cur = section.querySelector('.sk-porder');
  const next = html.querySelector('.sk-porder');
  if (cur && next) cur.replaceWith(next.cloneNode(true));
  else if (cur && !next) cur.remove();
  else if (!cur && next) {
    const buttons = form.querySelector('.product-form-buttons');
    const addButton = buttons?.querySelector('button[name="add"]');
    let anchor = buttons?.querySelector('[data-ship-manifest]') ?? addButton;
    while (anchor && anchor.parentElement !== buttons) anchor = anchor.parentElement;
    if (anchor) anchor.before(next.cloneNode(true));
  }
}

// A size switch morphs the whole <main>, so initSection runs again over a
// section whose nodes are a mix of survivors and replacements. Every bind is
// stamped on its own node: a survivor keeps its one listener, a replacement
// gets a fresh one, and nothing ever ends up bound twice (a double-bound
// submit would add the companion twice).
const bindOnce = (node) => {
  if (!node || node.__skPdpBound) return false;
  node.__skPdpBound = true;
  return true;
};

function initSection(section) {
  const addon = section.querySelector('[ref-pair-addon]');
  const form = section.querySelector('product-form-component form');

  if (bindOnce(addon)) {
    addon.addEventListener('click', () => {
      addon.classList.toggle('is-off');
      addon.setAttribute('aria-pressed', addon.classList.contains('is-off') ? 'false' : 'true');
      refreshTotals(section);
    });
  }

  if (form && bindOnce(form)) {
    const submitButton = form.querySelector('button[type="submit"][name="add"]');

    // Companion rides ahead of the native submit: the button's default action
    // hasn't run at click time, so add the companion, then submit once - the
    // drawer's section render comes from the native add and shows both lines.
    // pairBusy is set SYNCHRONOUSLY before the await so a double-click inside
    // the fetch window can't add the companion twice (reviewer H2).
    let pairBusy = false;
    submitButton?.addEventListener('click', async (event) => {
      const activeAddon = section.querySelector('[ref-pair-addon]:not(.is-off)');
      if (!activeAddon || form.dataset.pairHandled || pairBusy) return;
      pairBusy = true;
      event.preventDefault();
      try {
        await addCompanion(activeAddon);
        form.dataset.pairHandled = 'true';
        form.requestSubmit();
        setTimeout(() => delete form.dataset.pairHandled, 500);
      } finally {
        pairBusy = false;
      }
    });

    // Non-click submits (Enter key) still get the companion; the native add
    // is already in flight so the drawer render may lag one update (M1,
    // accepted - cart contents stay correct).
    form.addEventListener('submit', () => {
      if (form.dataset.pairHandled || pairBusy) return;
      const activeAddon = section.querySelector('[ref-pair-addon]:not(.is-off)');
      if (!activeAddon) return;
      pairBusy = true;
      form.dataset.pairHandled = 'true';
      addCompanion(activeAddon).finally(() => {
        pairBusy = false;
        setTimeout(() => delete form.dataset.pairHandled, 500);
      });
    });
  }

  refreshTotals(section);

  // Instant re-price at picker click (map is already current), then re-assert
  // after Horizon's own post-fetch morph work lands.
  const picker = section.querySelector('.variant-picker__form, variant-picker');
  if (bindOnce(picker)) {
    picker.addEventListener('change', () => {
      refreshTotals(section);
    });
  }

  if (!bindOnce(section)) return;

  // Horizon's own components may stamp the button label after this module
  // ran - one more assert after everything has loaded wins the last word.
  window.addEventListener('load', () => setTimeout(() => refreshTotals(section), 50), { once: true });

  section.addEventListener(StandardEvents.productSelect, (event) => {
    event.promise
      ?.then((payload) => {
        // Horizon morphs ONLY <variant-picker> itself - custom blocks never
        // re-render on variant switch (reviewer C1/H1). The picker's own
        // fetch already retrieved the full page (payload.detail.html):
        // replace the variant-priced server blocks from that document.
        const html = payload?.detail?.html;
        if (html) {
          for (const sel of ['.sk-coupon', '.sk-scarcity']) {
            const cur = section.querySelector(sel);
            const next = html.querySelector(sel);
            if (cur && next) cur.replaceWith(next.cloneNode(true));
            else if (cur && !next) cur.remove();
            else if (!cur && next && sel === '.sk-scarcity') {
              section.querySelector('.sk-assure')?.before(next.cloneNode(true));
            }
          }
          syncPreorderState(section, html);
          // Multi-option products carry no price map: the fetched page's own
          // strip holds the selected variant's server price.
          const nextStrip = html.querySelector('[data-coupon-strip]');
          if (nextStrip) repriceCouponStrips(section, parseInt(nextStrip.dataset.priceCents, 10));
        }
        setTimeout(() => refreshTotals(section), 0);
      })
      .catch(() => {});
  });
}

const scanSections = () => {
  for (const section of document.querySelectorAll('.shopify-section')) {
    if (section.querySelector('.sk-coupon, [data-coupon-strip], script[data-skullette-prices]')) initSection(section);
  }
};
scanSections();

// The size switcher swaps to a different product by morphing <main>; the
// nodes this module bound to may not survive that. bindOnce keeps survivors
// from doubling up, so a blanket re-scan is safe.
document.addEventListener('skullette:pdp:rebind', scanSections);

// The set picker only FIRES this event - it never touches the button. The
// one-tick defer lets any same-frame variant-change repaint land first, so
// this relabel always has the last word (two-writer flicker trap).
document.addEventListener('skullette:set:change', (event) => {
  // set picker ("Your set" checkboxes) or the set-offer card (per-design set page) - both fire this event
  const picker = document.querySelector('[data-set-picker], [data-set-offer]');
  const section = picker?.closest('.shopify-section, section');
  if (!section) return;
  const members = event.detail?.members ?? [];
  if (!members.length) {
    setVerbOverride = null;
  } else {
    const anyNow = !picker.hasAttribute('data-anchor-pre') || members.some((m) => !m.preB);
    setVerbOverride = anyNow ? 'Add to Bag' : 'Pre-order';
  }
  setTimeout(() => refreshTotals(section), 0);
});
